import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, User, LogOut, Settings as SettingsIcon, Users, Plus, Edit, Trash2,
    Search, Calendar, Clock, Award, Upload, X, Crown, Filter, GraduationCap,
    Book, Target, Trophy, Star, FileText, Image as ImageIcon, BarChart3,
    BookOpen, CheckCircle, Circle, TrendingUp, Download, Bell, Link as LinkIcon
} from 'lucide-react';
import { fetchEducationRecords, createEducationRecord, updateEducationRecord, deleteEducationRecord } from '../services/educationApi';
import MemberAvatar from './MemberAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';

const EducationLearning = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('timetables'); // timetables, reports, skills, plans, achievements
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [selectedMember, setSelectedMember] = useState('All');
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);
    const fileInputRef = useRef(null);

    // Timetables state
    const [timetables, setTimetables] = useState([]);
    const [showAddTimetableModal, setShowAddTimetableModal] = useState(false);
    const [editingTimetable, setEditingTimetable] = useState(null);
    const [newTimetable, setNewTimetable] = useState({
        memberId: '',
        institutionName: '',
        day: 'Monday',
        period: '',
        subject: '',
        teacher: '',
        startTime: '',
        endTime: '',
        notes: ''
    });

    // Report Cards state
    const [reportCards, setReportCards] = useState([]);
    const [showAddReportModal, setShowAddReportModal] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [newReport, setNewReport] = useState({
        memberId: '',
        term: 'Term 1',
        year: new Date().getFullYear().toString(),
        subjects: [{ name: '', grade: '', marks: '', maxMarks: '' }],
        comments: '',
        rank: '',
        percentage: '',
        attachments: []
    });

    // Skills state
    const [skills, setSkills] = useState([]);
    const [showAddSkillModal, setShowAddSkillModal] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);
    const [newSkill, setNewSkill] = useState({
        memberId: '',
        skillName: '',
        category: 'Music',
        level: 'Beginner',
        sessionsCompleted: 0,
        goalSessions: 0,
        mentorName: '',
        startDate: '',
        notes: '',
        attachments: []
    });

    // Learning Plans state
    const [learningPlans, setLearningPlans] = useState([]);
    const [showAddPlanModal, setShowAddPlanModal] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [newPlan, setNewPlan] = useState({
        memberId: '',
        title: '',
        goal: '',
        startDate: '',
        endDate: '',
        status: 'Active',
        modules: [{ title: '', targetDate: '', completed: false, resources: '' }]
    });

    // Achievements state
    const [achievements, setAchievements] = useState([]);
    const [showAddAchievementModal, setShowAddAchievementModal] = useState(false);
    const [editingAchievement, setEditingAchievement] = useState(null);
    const [newAchievement, setNewAchievement] = useState({
        memberId: '',
        achievementType: 'Academic',
        eventName: '',
        date: '',
        description: '',
        photo: null
    });

    // Load families
    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load education records when family changes
    useEffect(() => {
        const loadEducationData = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchEducationRecords(selectedFamily.id);
                const records = response.items || [];
                
                // Separate by type
                setTimetables(records.filter(r => r.record_type === 'timetable'));
                setReportCards(records.filter(r => r.record_type === 'report_card'));
                setSkills(records.filter(r => r.record_type === 'skill'));
                setLearningPlans(records.filter(r => r.record_type === 'learning_plan'));
                setAchievements(records.filter(r => r.record_type === 'achievement'));
            } catch (error) {
                console.error('Error loading education data:', error);
            }
        };
        loadEducationData();
    }, [selectedFamily]);

    const saveTimetables = (data) => {
        setTimetables(data);
    };

    const saveReportCards = (data) => {
        setReportCards(data);
    };

    const saveSkills = (data) => {
        setSkills(data);
    };

    const saveLearningPlans = (data) => {
        setLearningPlans(data);
    };

    const saveAchievements = (data) => {
        setAchievements(data);
    };

    const handleFamilySelect = (family) => {
        setSelectedFamily(family);
        localStorage.setItem('selectedFamilyId', family.id);
        setShowFamilyDropdown(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Timetable CRUD
    const handleAddTimetable = () => {
        if (!newTimetable.memberId || !newTimetable.subject) {
            alert('Please select member and enter subject');
            return;
        }

        const timetable = {
            ...newTimetable,
            id: Date.now(),
            createdAt: new Date().toISOString()
        };

        saveTimetables([...timetables, timetable]);
        resetTimetableForm();
        setShowAddTimetableModal(false);
    };

    const handleUpdateTimetable = () => {
        const updated = timetables.map(t => 
            t.id === editingTimetable.id ? { ...newTimetable, id: editingTimetable.id } : t
        );
        saveTimetables(updated);
        resetTimetableForm();
        setShowAddTimetableModal(false);
        setEditingTimetable(null);
    };

    const handleDeleteTimetable = (id) => {
        if (window.confirm('Delete this timetable entry?')) {
            saveTimetables(timetables.filter(t => t.id !== id));
        }
    };

    const resetTimetableForm = () => {
        setNewTimetable({
            memberId: '',
            institutionName: '',
            day: 'Monday',
            period: '',
            subject: '',
            teacher: '',
            startTime: '',
            endTime: '',
            notes: ''
        });
    };

    // Report Card CRUD
    const handleAddReport = () => {
        if (!newReport.memberId || !newReport.term) {
            alert('Please select member and term');
            return;
        }

        const report = {
            ...newReport,
            id: Date.now(),
            createdAt: new Date().toISOString()
        };

        saveReportCards([...reportCards, report]);
        resetReportForm();
        setShowAddReportModal(false);
    };

    const handleUpdateReport = () => {
        const updated = reportCards.map(r => 
            r.id === editingReport.id ? { ...newReport, id: editingReport.id } : r
        );
        saveReportCards(updated);
        resetReportForm();
        setShowAddReportModal(false);
        setEditingReport(null);
    };

    const handleDeleteReport = (id) => {
        if (window.confirm('Delete this report card?')) {
            saveReportCards(reportCards.filter(r => r.id !== id));
        }
    };

    const resetReportForm = () => {
        setNewReport({
            memberId: '',
            term: 'Term 1',
            year: new Date().getFullYear().toString(),
            subjects: [{ name: '', grade: '', marks: '', maxMarks: '' }],
            comments: '',
            rank: '',
            percentage: '',
            attachments: []
        });
    };

    const addSubject = () => {
        setNewReport({
            ...newReport,
            subjects: [...newReport.subjects, { name: '', grade: '', marks: '', maxMarks: '' }]
        });
    };

    const removeSubject = (index) => {
        const updated = newReport.subjects.filter((_, i) => i !== index);
        setNewReport({ ...newReport, subjects: updated });
    };

    const updateSubject = (index, field, value) => {
        const updated = newReport.subjects.map((sub, i) => 
            i === index ? { ...sub, [field]: value } : sub
        );
        setNewReport({ ...newReport, subjects: updated });
    };

    // Skill CRUD
    const handleAddSkill = () => {
        if (!newSkill.memberId || !newSkill.skillName) {
            alert('Please select member and enter skill name');
            return;
        }

        const skill = {
            ...newSkill,
            id: Date.now(),
            createdAt: new Date().toISOString()
        };

        saveSkills([...skills, skill]);
        resetSkillForm();
        setShowAddSkillModal(false);
    };

    const handleUpdateSkill = () => {
        const updated = skills.map(s => 
            s.id === editingSkill.id ? { ...newSkill, id: editingSkill.id } : s
        );
        saveSkills(updated);
        resetSkillForm();
        setShowAddSkillModal(false);
        setEditingSkill(null);
    };

    const handleDeleteSkill = (id) => {
        if (window.confirm('Delete this skill tracker?')) {
            saveSkills(skills.filter(s => s.id !== id));
        }
    };

    const resetSkillForm = () => {
        setNewSkill({
            memberId: '',
            skillName: '',
            category: 'Music',
            level: 'Beginner',
            sessionsCompleted: 0,
            goalSessions: 0,
            mentorName: '',
            startDate: '',
            notes: '',
            attachments: []
        });
    };

    // Learning Plan CRUD - will be similar pattern
    // Achievement CRUD - will be similar pattern

    // Constants
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const terms = ['Term 1', 'Term 2', 'Term 3', 'Annual'];
    const skillCategories = ['Music', 'Art', 'Sports', 'Coding', 'Dance', 'Language', 'Other'];
    const levels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
    const achievementTypes = ['Academic', 'Sports', 'Arts', 'Leadership', 'Community Service', 'Other'];
    
    const familyMembers = selectedFamily?.members?.filter(m => m && typeof m === 'object' && m.name) || [];
    
    const getMemberName = (memberId) => {
        return familyMembers[parseInt(memberId)]?.name || 'Unknown';
    };

    // Filter data by selected member
    const filteredTimetables = selectedMember === 'All' 
        ? timetables 
        : timetables.filter(t => t.memberId === selectedMember);

    const filteredReports = selectedMember === 'All'
        ? reportCards
        : reportCards.filter(r => r.memberId === selectedMember);

    const filteredSkills = selectedMember === 'All'
        ? skills
        : skills.filter(s => s.memberId === selectedMember);

    const filteredPlans = selectedMember === 'All'
        ? learningPlans
        : learningPlans.filter(p => p.memberId === selectedMember);

    const filteredAchievements = selectedMember === 'All'
        ? achievements
        : achievements.filter(a => a.memberId === selectedMember);

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
            {/* Navigation Bar */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all"
                                    style={{
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            <img
                                                src={selectedFamily.photo}
                                                alt={selectedFamily.name}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className="w-4 h-4" />
                                </button>

                                {showFamilyDropdown && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-200 z-50">
                                        {families.map((family) => (
                                            <button
                                                key={family.id}
                                                onClick={() => handleFamilySelect(family)}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                                            >
                                                {family.photo ? (
                                                    <img 
                                                        src={family.photo} 
                                                        alt={family.name}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 flex items-center justify-center">
                                                        <Users className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium">{family.name}</div>
                                                    {family.tagline && (
                                                        <div className="text-xs text-gray-500">{family.tagline}</div>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                        <div className="border-t border-gray-200 mt-2 pt-2">
                                            <button
                                                onClick={() => navigate('/famlis')}
                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-primary-blue font-medium"
                                            >
                                                + Add Family
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-primary-blue to-primary-pink flex items-center justify-center text-white font-semibold">
                                        U
                                    </div>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 border border-gray-100 z-50">
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <User className="w-5 h-5" />
                                            <span className="font-medium">Profile</span>
                                        </button>
                                        <button className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                                            <SettingsIcon className="w-5 h-5" />
                                            <span className="font-medium">Settings</span>
                                        </button>
                                        <button
                                            onClick={() => setShowPremiumModal(true)}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Crown className="w-5 h-5 text-yellow-500" />
                                            <span className="font-medium">Upgrade to Premium</span>
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Education & Learning</span>
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                    <div className="flex items-center gap-4 mb-4 md:mb-0">
                        <GraduationCap className="w-12 h-12 text-purple-600" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Education & Learning</h1>
                            <p className="text-gray-600">Track growth, progress, and achievements</p>
                        </div>
                    </div>

                    {/* Member Filter */}
                    <div className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-gray-500" />
                        <select
                            value={selectedMember}
                            onChange={(e) => setSelectedMember(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                            <option value="All">All Members</option>
                            {familyMembers.map((member, index) => (
                                <option key={index} value={index.toString()}>{member}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    <button
                        onClick={() => setActiveTab('timetables')}
                        className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                            activeTab === 'timetables'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>Timetables</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('reports')}
                        className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                            activeTab === 'reports'
                                ? 'bg-green-600 text-white shadow-md'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            <span>Report Cards</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('skills')}
                        className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                            activeTab === 'skills'
                                ? 'bg-purple-600 text-white shadow-md'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            <span>Skills</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('plans')}
                        className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                            activeTab === 'plans'
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5" />
                            <span>Learning Plans</span>
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('achievements')}
                        className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap transition-colors ${
                            activeTab === 'achievements'
                                ? 'bg-yellow-600 text-white shadow-md'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5" />
                            <span>Achievements</span>
                        </div>
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'timetables' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">School Timetables</h2>
                            <button
                                onClick={() => {
                                    setEditingTimetable(null);
                                    resetTimetableForm();
                                    setShowAddTimetableModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                                Add Entry
                            </button>
                        </div>

                        {filteredTimetables.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center">
                                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg">No timetable entries yet</p>
                                <p className="text-gray-400 mt-2">Add class schedules and timings</p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {days.map(day => {
                                    const dayTimetables = filteredTimetables.filter(t => t.day === day);
                                    if (dayTimetables.length === 0) return null;
                                    
                                    return (
                                        <div key={day} className="bg-white rounded-lg shadow-md p-6">
                                            <h3 className="text-xl font-bold text-gray-900 mb-4">{day}</h3>
                                            <div className="space-y-3">
                                                {dayTimetables.map(timetable => (
                                                    <div
                                                        key={timetable.id}
                                                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                    >
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <span className="font-semibold text-lg text-gray-900">
                                                                    {timetable.subject}
                                                                </span>
                                                                {timetable.period && (
                                                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                                        Period {timetable.period}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-sm text-gray-600 space-y-1">
                                                                <div className="flex items-center gap-2">
                                                                    <Clock className="w-4 h-4" />
                                                                    <span>{timetable.startTime} - {timetable.endTime}</span>
                                                                </div>
                                                                {timetable.teacher && (
                                                                    <div>Teacher: {timetable.teacher}</div>
                                                                )}
                                                                {timetable.institutionName && (
                                                                    <div>{timetable.institutionName}</div>
                                                                )}
                                                                <div className="text-purple-600 font-medium">
                                                                    {getMemberName(timetable.memberId)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingTimetable(timetable);
                                                                    setNewTimetable(timetable);
                                                                    setShowAddTimetableModal(true);
                                                                }}
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            >
                                                                <Edit className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTimetable(timetable.id)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Report Cards</h2>
                            <button
                                onClick={() => {
                                    setEditingReport(null);
                                    resetReportForm();
                                    setShowAddReportModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                                Add Report Card
                            </button>
                        </div>

                        {filteredReports.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center">
                                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg">No report cards yet</p>
                                <p className="text-gray-400 mt-2">Track academic performance</p>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {filteredReports.map(report => (
                                    <div key={report.id} className="bg-white rounded-lg shadow-md p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900">
                                                    {report.term} - {report.year}
                                                </h3>
                                                <p className="text-purple-600 font-medium mt-1">
                                                    {getMemberName(report.memberId)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {report.percentage && (
                                                    <span className="px-3 py-1 bg-green-100 text-green-700 font-bold rounded-lg">
                                                        {report.percentage}%
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        setEditingReport(report);
                                                        setNewReport(report);
                                                        setShowAddReportModal(true);
                                                    }}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <Edit className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteReport(report.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                            {report.subjects.filter(s => s.name).map((subject, idx) => (
                                                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                                                    <div className="font-medium text-gray-900">{subject.name}</div>
                                                    <div className="text-sm text-gray-600 mt-1">
                                                        Grade: <span className="font-semibold">{subject.grade}</span>
                                                    </div>
                                                    {subject.marks && subject.maxMarks && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {subject.marks}/{subject.maxMarks}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {report.rank && (
                                            <div className="text-sm text-gray-600 mb-2">
                                                Rank: <span className="font-semibold">{report.rank}</span>
                                            </div>
                                        )}

                                        {report.comments && (
                                            <div className="p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
                                                <strong>Comments:</strong> {report.comments}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'skills' && (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Skill Building</h2>
                            <button
                                onClick={() => {
                                    setEditingSkill(null);
                                    resetSkillForm();
                                    setShowAddSkillModal(true);
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                                Add Skill
                            </button>
                        </div>

                        {filteredSkills.length === 0 ? (
                            <div className="bg-white rounded-lg shadow-md p-12 text-center">
                                <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-gray-500 text-lg">No skills being tracked yet</p>
                                <p className="text-gray-400 mt-2">Track extracurricular progress</p>
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-4">
                                {filteredSkills.map(skill => {
                                    const progress = skill.goalSessions > 0 
                                        ? (skill.sessionsCompleted / skill.goalSessions) * 100 
                                        : 0;
                                    
                                    return (
                                        <div key={skill.id} className="bg-white rounded-lg shadow-md p-6">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-900">{skill.skillName}</h3>
                                                    <p className="text-purple-600 font-medium mt-1">
                                                        {getMemberName(skill.memberId)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setEditingSkill(skill);
                                                            setNewSkill(skill);
                                                            setShowAddSkillModal(true);
                                                        }}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteSkill(skill.id)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                                        {skill.category}
                                                    </span>
                                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                                                        {skill.level}
                                                    </span>
                                                </div>

                                                {skill.goalSessions > 0 && (
                                                    <div>
                                                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                                                            <span>Progress</span>
                                                            <span className="font-medium">
                                                                {skill.sessionsCompleted} / {skill.goalSessions} sessions
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                                            <div
                                                                className="bg-purple-600 h-2 rounded-full transition-all"
                                                                style={{ width: `${Math.min(progress, 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {skill.mentorName && (
                                                    <div className="text-sm text-gray-600">
                                                        <strong>Mentor:</strong> {skill.mentorName}
                                                    </div>
                                                )}

                                                {skill.startDate && (
                                                    <div className="text-sm text-gray-600">
                                                        <strong>Started:</strong> {new Date(skill.startDate).toLocaleDateString()}
                                                    </div>
                                                )}

                                                {skill.notes && (
                                                    <div className="p-3 bg-gray-50 rounded text-sm text-gray-700">
                                                        {skill.notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'plans' && (
                    <div className="text-center py-12">
                        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">Learning Plans</p>
                        <p className="text-gray-400 mt-2">Coming soon - Structured learning with milestones</p>
                    </div>
                )}

                {activeTab === 'achievements' && (
                    <div className="text-center py-12">
                        <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">Achievements & Certifications</p>
                        <p className="text-gray-400 mt-2">Coming soon - Celebrate accomplishments</p>
                    </div>
                )}
            </div>

            {/* Add Timetable Modal */}
            {showAddTimetableModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingTimetable ? 'Edit Timetable Entry' : 'Add Timetable Entry'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddTimetableModal(false);
                                    setEditingTimetable(null);
                                    resetTimetableForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Family Member *
                                </label>
                                <select
                                    value={newTimetable.memberId}
                                    onChange={(e) => setNewTimetable({ ...newTimetable, memberId: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Member</option>
                                    {familyMembers.map((member, index) => (
                                        <option key={index} value={index.toString()}>{member.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Institution Name
                                </label>
                                <input
                                    type="text"
                                    value={newTimetable.institutionName}
                                    onChange={(e) => setNewTimetable({ ...newTimetable, institutionName: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="School/College name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Day *
                                </label>
                                <select
                                    value={newTimetable.day}
                                    onChange={(e) => setNewTimetable({ ...newTimetable, day: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    {days.map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Period
                                    </label>
                                    <input
                                        type="text"
                                        value={newTimetable.period}
                                        onChange={(e) => setNewTimetable({ ...newTimetable, period: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="1, 2, 3..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        value={newTimetable.subject}
                                        onChange={(e) => setNewTimetable({ ...newTimetable, subject: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Mathematics"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Teacher
                                </label>
                                <input
                                    type="text"
                                    value={newTimetable.teacher}
                                    onChange={(e) => setNewTimetable({ ...newTimetable, teacher: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Teacher name"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Time *
                                    </label>
                                    <input
                                        type="time"
                                        value={newTimetable.startTime}
                                        onChange={(e) => setNewTimetable({ ...newTimetable, startTime: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Time *
                                    </label>
                                    <input
                                        type="time"
                                        value={newTimetable.endTime}
                                        onChange={(e) => setNewTimetable({ ...newTimetable, endTime: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={newTimetable.notes}
                                    onChange={(e) => setNewTimetable({ ...newTimetable, notes: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder="Additional notes"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowAddTimetableModal(false);
                                    setEditingTimetable(null);
                                    resetTimetableForm();
                                }}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingTimetable ? handleUpdateTimetable : handleAddTimetable}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                {editingTimetable ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Report Card Modal */}
            {showAddReportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingReport ? 'Edit Report Card' : 'Add Report Card'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddReportModal(false);
                                    setEditingReport(null);
                                    resetReportForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Family Member *
                                </label>
                                <select
                                    value={newReport.memberId}
                                    onChange={(e) => setNewReport({ ...newReport, memberId: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                >
                                    <option value="">Select Member</option>
                                    {familyMembers.map((member, index) => (
                                        <option key={index} value={index.toString()}>{member.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Term *
                                    </label>
                                    <select
                                        value={newReport.term}
                                        onChange={(e) => setNewReport({ ...newReport, term: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    >
                                        {terms.map(term => (
                                            <option key={term} value={term}>{term}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Year *
                                    </label>
                                    <input
                                        type="text"
                                        value={newReport.year}
                                        onChange={(e) => setNewReport({ ...newReport, year: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="2024"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Subjects & Grades
                                    </label>
                                    <button
                                        onClick={addSubject}
                                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                                    >
                                        + Add Subject
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {newReport.subjects.map((subject, index) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            <input
                                                type="text"
                                                value={subject.name}
                                                onChange={(e) => updateSubject(index, 'name', e.target.value)}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                placeholder="Subject name"
                                            />
                                            <input
                                                type="text"
                                                value={subject.grade}
                                                onChange={(e) => updateSubject(index, 'grade', e.target.value)}
                                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                placeholder="A+"
                                            />
                                            <input
                                                type="number"
                                                value={subject.marks}
                                                onChange={(e) => updateSubject(index, 'marks', e.target.value)}
                                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                placeholder="85"
                                            />
                                            <input
                                                type="number"
                                                value={subject.maxMarks}
                                                onChange={(e) => updateSubject(index, 'maxMarks', e.target.value)}
                                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                                placeholder="100"
                                            />
                                            {newReport.subjects.length > 1 && (
                                                <button
                                                    onClick={() => removeSubject(index)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Percentage
                                    </label>
                                    <input
                                        type="number"
                                        value={newReport.percentage}
                                        onChange={(e) => setNewReport({ ...newReport, percentage: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="85.5"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Rank
                                    </label>
                                    <input
                                        type="text"
                                        value={newReport.rank}
                                        onChange={(e) => setNewReport({ ...newReport, rank: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="5th"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Comments
                                </label>
                                <textarea
                                    value={newReport.comments}
                                    onChange={(e) => setNewReport({ ...newReport, comments: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    rows="3"
                                    placeholder="Teacher's comments..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowAddReportModal(false);
                                    setEditingReport(null);
                                    resetReportForm();
                                }}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingReport ? handleUpdateReport : handleAddReport}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                                {editingReport ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Skill Modal */}
            {showAddSkillModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingSkill ? 'Edit Skill' : 'Add Skill'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowAddSkillModal(false);
                                    setEditingSkill(null);
                                    resetSkillForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Family Member *
                                </label>
                                <select
                                    value={newSkill.memberId}
                                    onChange={(e) => setNewSkill({ ...newSkill, memberId: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="">Select Member</option>
                                    {familyMembers.map((member, index) => (
                                        <option key={index} value={index.toString()}>{member.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Skill Name *
                                </label>
                                <input
                                    type="text"
                                    value={newSkill.skillName}
                                    onChange={(e) => setNewSkill({ ...newSkill, skillName: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    placeholder="Piano, Painting, Coding..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={newSkill.category}
                                        onChange={(e) => setNewSkill({ ...newSkill, category: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        {skillCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Level
                                    </label>
                                    <select
                                        value={newSkill.level}
                                        onChange={(e) => setNewSkill({ ...newSkill, level: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    >
                                        {levels.map(level => (
                                            <option key={level} value={level}>{level}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Sessions Completed
                                    </label>
                                    <input
                                        type="number"
                                        value={newSkill.sessionsCompleted}
                                        onChange={(e) => setNewSkill({ ...newSkill, sessionsCompleted: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        min="0"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Goal Sessions
                                    </label>
                                    <input
                                        type="number"
                                        value={newSkill.goalSessions}
                                        onChange={(e) => setNewSkill({ ...newSkill, goalSessions: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Mentor Name
                                </label>
                                <input
                                    type="text"
                                    value={newSkill.mentorName}
                                    onChange={(e) => setNewSkill({ ...newSkill, mentorName: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    placeholder="Instructor or coach name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    value={newSkill.startDate}
                                    onChange={(e) => setNewSkill({ ...newSkill, startDate: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={newSkill.notes}
                                    onChange={(e) => setNewSkill({ ...newSkill, notes: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    rows="3"
                                    placeholder="Goals, achievements, milestones..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowAddSkillModal(false);
                                    setEditingSkill(null);
                                    resetSkillForm();
                                }}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingSkill ? handleUpdateSkill : handleAddSkill}
                                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                {editingSkill ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default EducationLearning;

